var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { Option } from './option';
import { LiveRegion } from '../../live-region/src';
import fitDropdown, { saveContainersScrollPosition, restoreContainersScrollPosition } from './utils/dropdown-fit-handler';
import { getOptionId, getOptionGroup, isOptionSelected, getHighlightedId } from './utils/options';
var lastGeneratedId = 0;
var nextGeneratedId = function () { return "awsui-select-dropdown-" + lastGeneratedId++; };
export var Dropdown = AwsUi.__addComponent('awsui-select-dropdown', {
    releaseStatus: 'internal',
    properties: {
        // Array of objects, each can represent a simple option with the following properties:
        //
        // * `label` [string]: A short title shown in the select to describe this item.
        // * `disabled` [boolean]: Whether this item is disabled.
        //
        // Extended options can provide further guidance about each option by specifying further
        // properties:
        // * `id` [string]: The id of the select when this item is selected.
        // * `label` [string]: the label describing the option.
        // * `labelTag` [string]: A tag giving further guidance shown pulled right next to the label.
        // * `description` [string]: Further explanatory guidance about the option below the label.
        // * `iconName` [string]: The name of the icon, use with awsui icons.
        // * `iconUrl` [string]: The url to the icon, use with custom icons.
        // * `iconAlt` [string]: The alternate text, use with custom icons.
        // * `tags` [string array]: A list of tags giving further guidance about the option.
        // * `filteringTags` [string array]: A list of metadata that is never displayed unless one of
        // the tags gives a match for the user filtering.
        //
        // Options can be grouped by specifying the given option as a group of options:
        //
        // * `label` [string]: description of the group.
        // * `options` [array]: array of simple options.
        // * `disabled` [boolean]: whether the whole group is disabled.
        //
        // Notice that only one level of options nesting is supported.
        options: {
            type: 'array',
            itemTypes: ['Dropdown.IOption', 'Dropdown.Option', 'Dropdown.OptionsGroup']
        },
        // A flag indicating whether the dropdown is open.
        open: {
            type: 'boolean',
            onChange: function (oldValue, newValue) {
                if (newValue) {
                    this.__delayedScrollToActiveOption();
                }
            }
        },
        // The selected options.
        selectedOptions: {
            type: 'array',
            itemTypes: ['Dropdown.Option']
        },
        // The highlighted option.
        highlightedOption: {
            type: 'object',
            objectType: 'Dropdown.Option',
            onChange: function (oldValue, newValue) {
                var newParentGroup = getOptionGroup(newValue, this.options);
                var oldParentGroup = getOptionGroup(oldValue, this.options);
                if (newParentGroup !== oldParentGroup) {
                    this._parentGroup = newParentGroup;
                }
                else {
                    this._parentGroup = undefined;
                }
                this.__delayedScrollToActiveOption();
            }
        },
        // The text to highlight in options.
        highlightText: {
            type: 'string'
        },
        // Id for the dropdown options.
        controlId: {
            type: 'string'
        },
        // Whether the dropdown is inside a Multiselect component
        isMultiselect: {
            type: 'boolean'
        },
        // Whether option filtering is enabled.
        hasFiltering: {
            type: 'boolean'
        },
        // Whether to display checkboxes in dropdown options.
        // The default value is false.
        checkboxes: {
            type: 'boolean',
            defaultValue: false
        },
        // If defined, focus this option instead of selectedOption.
        // This is used to in Multiselect with keepOpen to avoid scrolling after selecting an option.
        focusedOption: {
            type: 'object',
            objectType: 'Dropdown.Option',
            onChange: function () {
                this.__delayedScrollToActiveOption();
            }
        },
        // Adds aria-required on the list element
        ariaRequired: {
            type: 'boolean'
        },
        // Localized string that describes an option as selected.
        selectedLabel: {
            type: 'string'
        },
        // Optionally overrides the text that is announced to screen readers
        // when the highlighted option changes. By default, this announces
        // the option's name and properties, as well as its selected state if
        // the selectedLabel property is defined.
        highlightedOptionDescription: {
            type: 'function',
            functionType: 'Dropdown.HighlightedOptionDescription'
        }
    },
    regions: {
        // Region that is supposed to contain a filtering input
        filter: {},
        // Displayed at the bottom of the scrollable list
        listBottom: {},
        // Displayed at the bottom of the dropdown in sticky mode
        footer: {}
    },
    functions: {
        // Helper function that returns whether a given options group is disabled.
        __isOptionsGroupDisabled: function (group) {
            if (group.disabled) {
                return true;
            }
            var hasEnabledOption = group.options.some(function (option) { return !option.disabled; });
            if (hasEnabledOption || group.options.length === 0) {
                return false;
            }
            return true;
        },
        __getOptionsListId: function () {
            return this.controlId + "-options";
        },
        __getDropdownContainer: function () {
            return this.hasFiltering
                ? this.node.querySelector('.awsui-select-dropdown')
                : this.node.querySelector("#" + this.__getOptionsListId());
        },
        __resetScrollTop: function () {
            var dropdown = this.node.querySelector('.awsui-select-dropdown-options-container');
            if (dropdown) {
                dropdown.scrollTop = 0;
            }
        },
        __delayedScrollToActiveOption: function () {
            var _this = this;
            if (this.__scrollToActiveOptionTimeout) {
                clearTimeout(this.__scrollToActiveOptionTimeout);
            }
            this.__scrollToActiveOptionTimeout = setTimeout(function () { return _this.__scrollToActiveOption(); });
        },
        __scrollToActiveOption: function () {
            this.__scrollToActiveOptionTimeout = null;
            var node = this.node;
            var optionsContainer = node.querySelector('.awsui-select-dropdown-options-container');
            var focusedOption = node.querySelector('.awsui-select-dropdown-option-focused');
            var highlightedOption = node.querySelector('.awsui-select-dropdown-option-highlighted');
            var selectedOption = node.querySelector('.awsui-select-dropdown-option-selected');
            var scrollToOption = highlightedOption || focusedOption || selectedOption;
            if (!scrollToOption) {
                return;
            }
            // only scroll if the option is not in view already
            if (scrollToOption.offsetTop < optionsContainer.scrollTop ||
                scrollToOption.offsetTop + scrollToOption.clientHeight >
                    optionsContainer.scrollTop + optionsContainer.clientHeight) {
                optionsContainer.scrollTop = scrollToOption.offsetTop;
            }
        },
        focusDropdown: function () {
            var listbox = this.__getDropdownContainer();
            if (listbox) {
                var savedScrollPositions = saveContainersScrollPosition(listbox, listbox.parentElement);
                listbox.focus();
                // In Safari we need to restore the scrolling position
                // of the parent containers after focusing
                restoreContainersScrollPosition(savedScrollPositions);
            }
        }
    },
    callbacks: {
        initialized: function () {
            if (!this.controlId) {
                this.controlId = nextGeneratedId();
            }
        }
    },
    events: {
        // Fired when an enabled option is selected.
        __selected: {
            bubbles: true,
            cancelable: false,
            detailType: 'Dropdown.Option'
        },
        loadMore: {
            bubbles: true,
            cancelable: false
        }
    },
    render: function (m, h) {
        var _this = this;
        var mainContainerClass = new LegacyClassBuilder(this)
            .addClass('awsui-select-dropdown')
            .addComponentClass('open', this.open)
            .addComponentClass('has-extra-region', this.footer || this.listBottom)
            .addComponentClass('has-content', this.options.length > 0);
        var mainContainerAttr = {
            className: mainContainerClass.toClassName(),
            id: this.controlId
        };
        if (!this.open) {
            // we need to display these regions because they might be filled with embedded components.
            // Embedded components can be used only within elements that have a parent component.
            return (AwsUi.__createElement("div", __assign({}, mainContainerAttr),
                h.region('filter', 'div'),
                h.region('listBottom'),
                h.region('footer', 'div')));
        }
        var prefixClass = function (className) {
            return _this.__componentName + "-" + className;
        };
        var optionCounter = 0;
        var optionListElement = function (options, group) {
            if (options.length === 0) {
                return null;
            }
            var optionList = options.map(function (option) {
                if (option.options) {
                    return optionsGroupElement(option);
                }
                return optionElement(option, group);
            });
            var additionalListAttrs = !_this.hasFiltering
                ? {
                    tabIndex: -1,
                    'aria-required': _this.ariaRequired
                }
                : {};
            if (!_this.hasFiltering && !group) {
                additionalListAttrs['aria-activedescendant'] = getHighlightedId(_this.controlId, _this.options, _this.highlightedOption);
            }
            if (_this.isMultiselect && !group) {
                additionalListAttrs['aria-multiselectable'] = 'true';
            }
            if (!group) {
                additionalListAttrs['id'] = _this.__getOptionsListId();
            }
            return (AwsUi.__createElement("ul", __assign({ className: prefixClass('options'), 
                // Groups inside comboxbox/listbox are an unsupported pattern in ARIA as of now.
                // There is an official draft for the upcoming ARIA 1.2 spec: https://github.com/w3c/aria/issues/719
                // For now, we will need to flatten the group hierarchy using role="presentation" and rely on
                // labeling techniques to convey the information of groups to dropdown items.
                // Backlog item: https://issues.amazon.com/issues/AWSUI-6554
                role: group ? 'presentation' : 'listbox' }, additionalListAttrs), optionList));
        };
        var handleSelected = function (option) {
            _this.__fireEvent('__selected', option);
        };
        var handleScroll = function (event) {
            var BOTTOM_TRIGGER_OFFSET = 80;
            var scrollContainer = event.target;
            var bottomEdgePosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
            var remainingScrollHeight = scrollContainer.scrollHeight - bottomEdgePosition;
            if (remainingScrollHeight < BOTTOM_TRIGGER_OFFSET) {
                _this.__fireEvent('loadMore');
            }
        };
        var optionElement = function (option, group) {
            var optionId = getOptionId(_this.controlId, optionCounter++);
            var isDisabled = option.disabled || (group && group.disabled);
            var isSelected = isOptionSelected(option, _this.selectedOptions);
            var cb = new LegacyClassBuilder(_this)
                .addClass('awsui-select-dropdown-option')
                .addComponentClass('option-selected', isSelected)
                .addComponentClass('option-selectable', !isDisabled)
                .addComponentClass('option-highlighted', option === _this.highlightedOption)
                .addComponentClass('option-focused', option === _this.focusedOption)
                .addComponentClass('option-disabled', isDisabled);
            var attr = {
                id: optionId,
                role: 'option'
            };
            if (isSelected) {
                attr['aria-selected'] = 'true';
            }
            if (isDisabled) {
                attr['aria-disabled'] = 'true';
            }
            return (AwsUi.__createElement("li", __assign({ className: cb.toClassName() }, attr),
                AwsUi.__createElement(Option, { option: option, disabled: isDisabled, highlighted: option === _this.highlightedOption, selected: isSelected, highlightText: _this.highlightText, selectable: !isDisabled, onSelected: handleSelected, hasCheckbox: _this.checkboxes }),
                isSelected && AwsUi.__createElement("div", { class: "awsui-util-hide" }, _this.selectedLabel)));
        };
        var optionsGroupElement = function (group) {
            var isDisabled = _this.__isOptionsGroupDisabled(group);
            var cb = new LegacyClassBuilder(_this)
                .addComponentClass('options-group')
                .addComponentClass('options-group-disabled', isDisabled);
            var attr = {};
            if (isDisabled) {
                attr['aria-disabled'] = 'true';
            }
            return (AwsUi.__createElement("li", __assign({ className: cb.toClassName(), role: "presentation" }, attr),
                AwsUi.__createElement("p", { className: prefixClass('options-group-header') }, group.label),
                optionListElement(group.options, group)));
        };
        var defaultOptionDescription = function (option, parentGroup) {
            var isSelected = isOptionSelected(option, _this.selectedOptions);
            var messageParts = __spreadArrays([
                parentGroup && parentGroup.label,
                option.value,
                option.label,
                option.description,
                option.labelTag
            ], (option.tags || []), [
                isSelected ? _this.selectedLabel : null
            ]).filter(function (el) { return !!el; });
            return messageParts.join(' ');
        };
        var afterRender = function (element) { return fitDropdown(element.parentElement, function () { return _this.__fireEvent('loadMore'); }); };
        var highlightedOptionDescription = this.highlightedOptionDescription || defaultOptionDescription;
        return (AwsUi.__createElement("div", __assign({}, mainContainerAttr),
            h.region('filter', 'div'),
            AwsUi.__createElement("div", { className: prefixClass('options-container'), onscroll: handleScroll },
                optionListElement(this.options),
                h.region('listBottom', 'div', { className: prefixClass('list-bottom') })),
            h.region('footer', 'div', { className: prefixClass('footer') }),
            AwsUi.__createElement("div", { lifecycleKey: "awsui-select-dropdown", "aria-hidden": "true", componentDidMount: afterRender, componentDidUpdate: afterRender }),
            AwsUi.__createElement(LiveRegion, { message: this.highlightedOption ? highlightedOptionDescription(this.highlightedOption, this._parentGroup) : '' })));
    }
});
//# sourceMappingURL=dropdown.js.map